import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { message, Modal } from 'antd'; // Add antd message and Modal
import './LibraryBlockedSlots.scss';

const basePath = '/api/admin/library-appointments';

const LibraryBlockedSlots = () => {
    const [blockedSlots, setBlockedSlots] = useState([]);
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(false);

    // Form state - multi-date and multi-slot
    const [selectedDates, setSelectedDates] = useState([]);
    const [selectedSlots, setSelectedSlots] = useState([]);
    const [reason, setReason] = useState('');

    // Calendar state
    const [calendarMonth, setCalendarMonth] = useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), 1);
    });

    // Dynamically generated slot options based on settings
    const [slotOptions, setSlotOptions] = useState([]);

    useEffect(() => {
        loadSettings();
        loadBlockedSlots();
    }, []);

    useEffect(() => {
        if (settings) {
            generateSlotOptions();
        }
    }, [settings]);

    const loadSettings = async () => {
        try {
            const res = await api.get('/api/library-appointments/settings');
            if (res?.isSuccess && res?.data) {
                setSettings(res.data);
            }
        } catch (err) {
            console.error('Settings load error:', err);
        }
    };

    const generateSlotOptions = () => {
        if (!settings?.workingDayStartTime || !settings?.workingDayEndTime) {
            setSlotOptions([]);
            return;
        }

        const parseTime = (timeStr) => {
            const [h, m] = timeStr.split(':').map(Number);
            return h * 60 + m;
        };

        const formatTime = (minutes) => {
            const h = Math.floor(minutes / 60);
            const m = minutes % 60;
            return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        };

        const startMin = parseTime(settings.workingDayStartTime);
        const endMin = parseTime(settings.workingDayEndTime);
        const duration = Number(settings.slotDurationMinutes) || 240;

        const totalMinutes = endMin - startMin;
        const fullSlotCount = Math.floor(totalMinutes / duration);
        const remainder = totalMinutes % duration;

        const options = [];
        let current = startMin;

        for (let i = 0; i < fullSlotCount; i++) {
            let slotEnd = current + duration;
            if (i === fullSlotCount - 1 && remainder > 0) {
                slotEnd += remainder;
            }

            const startLabel = formatTime(current);
            const endLabel = formatTime(slotEnd);

            options.push({
                start: startLabel,
                end: endLabel,
                label: `${startLabel} - ${endLabel}`,
                key: `${startLabel}-${endLabel}`,
            });

            current = slotEnd;
        }

        if (options.length === 0 && totalMinutes > 0) {
            const startLabel = formatTime(startMin);
            const endLabel = formatTime(endMin);
            options.push({
                start: startLabel,
                end: endLabel,
                label: `${startLabel} - ${endLabel}`,
                key: `${startLabel}-${endLabel}`,
            });
        }

        setSlotOptions(options);
    };

    const loadBlockedSlots = async () => {
        setLoading(true);
        try {
            const res = await api.get(`${basePath}/blocked-slots`);
            if (res?.isSuccess && res?.data) {
                setBlockedSlots(res.data);
            }
        } catch (err) {
            message.error('Kapalı aralıklar yüklenemedi.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Calendar helpers
    const calendarDays = useMemo(() => {
        const year = calendarMonth.getFullYear();
        const month = calendarMonth.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const days = [];
        const startPadding = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;

        // Padding days from previous month
        for (let i = 0; i < startPadding; i++) {
            days.push({ date: null, disabled: true });
        }

        // Current month days
        for (let d = 1; d <= lastDay.getDate(); d++) {
            const date = new Date(year, month, d);
            const dateStr = date.toISOString().split('T')[0];
            const isPast = date < today;
            days.push({
                date: dateStr,
                day: d,
                disabled: isPast,
                isToday: date.getTime() === today.getTime(),
            });
        }

        return days;
    }, [calendarMonth]);

    const toggleDate = (dateStr) => {
        if (!dateStr) return;
        setSelectedDates((prev) =>
            prev.includes(dateStr)
                ? prev.filter((d) => d !== dateStr)
                : [...prev, dateStr].sort()
        );
    };

    const toggleSlot = (slotKey) => {
        setSelectedSlots((prev) =>
            prev.includes(slotKey)
                ? prev.filter((s) => s !== slotKey)
                : [...prev, slotKey]
        );
    };

    const selectAllSlots = () => {
        if (selectedSlots.length === slotOptions.length) {
            setSelectedSlots([]);
        } else {
            setSelectedSlots(slotOptions.map((s) => s.key));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();


        if (selectedDates.length === 0) {
            message.warning('Lütfen en az bir tarih seçin.');
            return;
        }
        if (selectedSlots.length === 0) {
            message.warning('Lütfen en az bir randevu saati seçin.');
            return;
        }

        setLoading(true);
        let successCount = 0;
        let errorCount = 0;
        const createdBy = localStorage.getItem('username') || 'Admin';

        try {
            for (const dateStr of selectedDates) {
                for (const slotKey of selectedSlots) {
                    const slot = slotOptions.find((s) => s.key === slotKey);
                    if (!slot) continue;

                    const startTime = slot.start.length === 5 ? `${slot.start}:00` : slot.start;
                    const endTime = slot.end.length === 5 ? `${slot.end}:00` : slot.end;

                    try {
                        const res = await api.post(`${basePath}/blocked-slots`, {
                            blockedDate: dateStr,
                            slotStartTime: startTime,
                            slotEndTime: endTime,
                            reason: reason || null,
                            createdBy,
                        });

                        if (res?.isSuccess) {
                            successCount++;
                        } else {
                            errorCount++;
                        }
                    } catch {
                        errorCount++;
                    }
                }
            }

            if (successCount > 0) {
                message.success(`${successCount} aralık başarıyla kapatıldı.${errorCount > 0 ? ` ${errorCount} aralık zaten kapalıydı.` : ''}`);
                setSelectedDates([]);
                setSelectedSlots([]);
                setReason('');
                loadBlockedSlots();
            } else {
                message.error('Hiçbir aralık kapatılamadı. Seçilen aralıklar zaten kapalı olabilir.');
            }
        } catch (err) {
            message.error('Bir hata oluştu.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        Modal.confirm({
            title: 'Kayıt Silme',
            content: 'Bu kapalı aralığı silmek istediğinizden emin misiniz?',
            okText: 'Evet',
            okType: 'danger',
            cancelText: 'Hayır',
            onOk: async () => {
                setLoading(true);
                try {
                    const res = await api.delete(`${basePath}/blocked-slots/${id}`);
                    if (res?.isSuccess) {
                        message.success('Aralık başarıyla silindi.');
                        loadBlockedSlots();
                    } else {
                        message.error(res?.resultMessage || 'Silme işlemi başarısız oldu.');
                    }
                } catch (err) {
                    message.error(err.response?.data?.resultMessage || 'Bir hata oluştu.');
                    console.error(err);
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const prevMonth = () => {
        setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    };

    const nextMonth = () => {
        setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    };

    const monthNames = [
        'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
        'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
    ];

    return (
        <div className="library-blocked-slots-page">
            <div className="page-header">
                <h1>Kapalı Randevu Aralıkları</h1>
                <p>Birden fazla tarih ve saat seçerek toplu kapatma yapabilirsiniz.</p>
            </div>



            <div className="content-grid">
                {/* Add New Blocked Slot Form */}
                <section className="card add-blocked-slot-form">
                    <h2>Yeni Kapalı Aralık Ekle</h2>
                    <form onSubmit={handleSubmit}>
                        {/* Calendar for multi-date selection */}
                        <div className="form-group">
                            <label>Tarih Seçimi ({selectedDates.length} gün seçildi)</label>
                            <div className="calendar-widget">
                                <div className="calendar-header">
                                    <button type="button" onClick={prevMonth} className="calendar-nav">&lt;</button>
                                    <span className="calendar-title">
                                        {monthNames[calendarMonth.getMonth()]} {calendarMonth.getFullYear()}
                                    </span>
                                    <button type="button" onClick={nextMonth} className="calendar-nav">&gt;</button>
                                </div>
                                <div className="calendar-weekdays">
                                    <span>Pt</span><span>Sa</span><span>Ça</span>
                                    <span>Pe</span><span>Cu</span><span>Ct</span><span>Pz</span>
                                </div>
                                <div className="calendar-days">
                                    {calendarDays.map((d, idx) => (
                                        <button
                                            key={idx}
                                            type="button"
                                            className={[
                                                'calendar-day',
                                                d.disabled ? 'disabled' : '',
                                                d.isToday ? 'today' : '',
                                                d.date && selectedDates.includes(d.date) ? 'selected' : '',
                                            ].filter(Boolean).join(' ')}
                                            onClick={() => !d.disabled && toggleDate(d.date)}
                                            disabled={d.disabled}
                                        >
                                            {d.day || ''}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {selectedDates.length > 0 && (
                                <div className="selected-dates-display">
                                    <strong>Seçili günler:</strong>{' '}
                                    {selectedDates.map((d) => new Date(d).toLocaleDateString('tr-TR')).join(', ')}
                                </div>
                            )}
                        </div>

                        {/* Slot checkboxes */}
                        <div className="form-group">
                            <label>
                                Randevu Saatleri
                                {slotOptions.length > 0 && (
                                    <button
                                        type="button"
                                        className="select-all-btn"
                                        onClick={selectAllSlots}
                                    >
                                        {selectedSlots.length === slotOptions.length ? 'Tümünü Kaldır' : 'Tümünü Seç'}
                                    </button>
                                )}
                            </label>
                            {slotOptions.length > 0 ? (
                                <div className="slot-checkboxes">
                                    {slotOptions.map((slot, idx) => (
                                        <label key={slot.key} className="slot-checkbox">
                                            <input
                                                type="checkbox"
                                                checked={selectedSlots.includes(slot.key)}
                                                onChange={() => toggleSlot(slot.key)}
                                            />
                                            <span className="slot-label">
                                                <span className="slot-number">{idx + 1}. Randevu</span>
                                                <span className="slot-time">{slot.label}</span>
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            ) : (
                                <small className="form-hint">Mesai saatleri tanımlanmadı.</small>
                            )}
                        </div>

                        <div className="form-group">
                            <label htmlFor="reason">Mail Mesajı</label>
                            <textarea
                                id="reason"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="Randevusu iptal edilen kullanıcılara gönderilecek mesaj"
                                rows="2"
                            />
                        </div>

                        <button type="submit" className="btn btn-primary" disabled={loading || selectedDates.length === 0 || selectedSlots.length === 0}>
                            {loading ? 'İşleniyor...' : `${selectedDates.length * selectedSlots.length} Aralığı Kapat`}
                        </button>
                    </form>
                </section>

                {/* Blocked Slots List */}
                <section className="card blocked-slots-list">
                    <h2>Mevcut Kapalı Aralıklar</h2>
                    {loading && <p>Yükleniyor...</p>}
                    {!loading && blockedSlots.length === 0 && (
                        <p className="empty-state">Henüz kapalı aralık bulunmuyor.</p>
                    )}
                    {!loading && blockedSlots.length > 0 && (
                        <div className="table-responsive">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Tarih</th>
                                        <th>Saat Aralığı</th>
                                        <th>Neden</th>
                                        <th>Oluşturan</th>
                                        <th>Oluşturma Tarihi</th>
                                        <th>İşlemler</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {blockedSlots.map((slot) => (
                                        <tr key={slot.id}>
                                            <td>{new Date(slot.blockedDate).toLocaleDateString('tr-TR')}</td>
                                            <td>
                                                {slot.slotStartTime?.substring(0, 5)} - {slot.slotEndTime?.substring(0, 5)}
                                            </td>
                                            <td>{slot.reason || '-'}</td>
                                            <td>{slot.createdBy || '-'}</td>
                                            <td>{new Date(slot.createdAt).toLocaleDateString('tr-TR')}</td>
                                            <td>
                                                <button
                                                    className="btn btn-danger btn-sm"
                                                    onClick={() => handleDelete(slot.id)}
                                                    disabled={loading}
                                                >
                                                    Sil
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
};

export default LibraryBlockedSlots;
